/**
 * ProofSpec — what a product declares to have its proof page: the words, the sections in order, the search query,
 * the catalogue slugs and the chips. Everything else (the page, the mail, the section builder) is this folder's. The
 * page is general: every visitor sees the same sections, filled from pictures the product fixes (`ProofSource`).
 * A spec is data; the model copies its words into `ProofData` so the page and the mail render from the data alone
 * and a spec that reads server-only constants (Ovid's closing reads the allowance) never reaches a client bundle.
 */

/** The closed set of section visuals the core renders on the page and in the mail. */
export type DemoKind =
    | 'catalogue' // a mosaic of what the product holds
    | 'search' // a query and its results
    | 'pair' // a basis picture beside the same setting with a made-up person's face
    | 'edit' // before / direction / after, one or several pairs
    | 'scratch' // the words, then the picture, no basis
    | 'blank' // a ready picture armed in the product's own wall bar, with the field and the verb it offers
    | 'themes' // a row of themes, a picture each, with the catalogue's counts
    | 'neighbours' // one picture beside the pictures around it on the wall
    | 'muse' // a few muses, each one identity across several scenes
    | 'ownMuse' // how a muse is made from photos: the photos in, her being built, her in scenes
    | 'sessions' // audio the product plays: a row of sessions, each with its title, who it is written for, its length
    | 'listen' // sessions with the words they speak printed beside the player
    | 'steps' // the product's own interface in order, one screen per step
    | 'note' // a few short lines and no picture
    | 'links' // pages of the site, each with a line saying what it is

/** `steps` are the labels of a visual that shows its picture in stages (`ownMuse`). */
export type DemoSpec = {kind: DemoKind; title: string; body?: string; steps?: readonly string[]}

/** Words with a `{name}` placeholder where a made-up person's name goes. */
export type ProofCopy = {
    hero: {title: string; line: string | null; /** A door into the product, under the hero's words. */ cta?: {label: string; to: string}}
    /** The mail's subject and headline. */
    mailHeadline: string
    demosHeading: string | null
    /** Said when the sections show a made-up person. */
    shownOn: string | null
    chipsHeading: string | null
    picksHeading: string
    closing: {title: string | null; body: string | null; link: string; to: string}
    tags: {
        pairFrom: string
        pairWithOn: string
        before: string
        after: string
        packs: string
        pictures: string
        /** The label of a link that plays a session on the page, in the mail. */
        listen?: string
    }
    alts: {sample: string; shown: string; pairWithOn: string; editBefore: string; editAfter: string}
}

export type ProofSpec = {
    product: 'narcis' | 'toile' | 'ovid' | 'quies'
    /** The host the mail's relative image paths are made absolute against. */
    domain: string
    searchQuery: string
    catalogueSlugs: readonly string[]
    chips: ReadonlyArray<{label: string; slug: string}>
    demos: readonly DemoSpec[]
    copy: ProofCopy
}

/** What the page and the mail read of a spec, carried in `ProofData`. */
export type ProofView = {product: ProofSpec['product']; domain: string; demos: readonly DemoSpec[]; copy: ProofCopy}

export const viewOf = ({product, domain, demos, copy}: ProofSpec): ProofView => ({product, domain, demos, copy})

export const fill = (template: string, name: string | null) => template.replace('{name}', name ?? '')
